import React, { useEffect, useState } from 'react';
import { AccessibilityInfo, Animated, Easing, Platform, StyleSheet, Text } from 'react-native';
import { unseenReactionBadgeModel } from '../../util/unseenReactionBadge';
import { ReactionGlyph, hasReactionGlyph } from './ReactionGlyph';

/**
 * Значок непросмотренной реакции в строке списка чатов (заказ владельца 2026-09-09).
 *
 * <p><b>Зачем.</b> Реакция — это ответ на твоё сообщение, но до сих пор о нём можно было
 * узнать, только открыв переписку и долистав до места. Значок переносит новость туда, где
 * человек и так скользит глазами, — в список чатов.
 *
 * <p><b>Почему иконка реакции, а не просто точка.</b> «❤️» и «👎» — разные новости, и знать,
 * какая именно тебя ждёт, полезно до входа в чат. Показывается самая свежая; числа рядом нет
 * (решение владельца 2026-09-09) — значок отвечает «на тебя отреагировали», а сколько раз и
 * кто, видно в самой переписке.
 *
 * <p><b>Почему пульс конечный, а не вечное мигание.</b> Список скроллится, и постоянно
 * дёргающийся элемент в нём утомляет, а анимация не даёт экрану успокоиться. Поэтому
 * {@link PULSES} затуханий при появлении — чтобы поймать взгляд, — и дальше статичный значок,
 * который висит до открытия чата. При включённом «уменьшении движения» пульса нет вовсе:
 * системная настройка ровно про это, а сам значок остаётся видимым.
 *
 * <p><b>Про рисовку.</b> Реакция рисуется линией — {@link ReactionGlyph}, тем же способом и
 * в том же тоне, что колокольчик и молния рядом. Обесцвеченная фильтром эмодзи, с которой
 * значок начинался, эту задачу не решала: у неё остаются тени, градиенты и толстые заливки,
 * и рядом со штриховой графикой она читалась выцветшим стикером. Эмодзи осталась запасным
 * путём для реакций вне рисованного набора; тогда её всё же обесцвечиваем, и синтаксис
 * {@code filter} у платформ разный — см. {@code styles.glyph}.
 */
const PULSES = 4;
const PULSE_MS = 620;
const DIM_OPACITY = 0.25;
const GLYPH_SIZE = 20;

export function UnseenReactionBadge({
  emoji,
  color,
  accessibilityLabel,
}: {
  emoji: string;
  /** Цвет линии иконки — берётся из палитры списка, чтобы не спорить с темой. */
  color: string;
  accessibilityLabel: string;
}) {
  const [opacity] = useState(() => new Animated.Value(1));
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    let alive = true;
    void AccessibilityInfo.isReduceMotionEnabled().then((on) => {
      if (alive) setReduceMotion(on);
    });
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', (on) => {
      if (alive) setReduceMotion(on);
    });
    return () => {
      alive = false;
      sub.remove();
    };
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      opacity.setValue(1);
      return;
    }
    // Ключ по эмодзи в родителе перезапустит компонент на новой реакции, поэтому пульс
    // достаточно завести один раз на монтирование.
    const step = (toValue: number) =>
      Animated.timing(opacity, {
        toValue,
        duration: PULSE_MS / 2,
        easing: Easing.inOut(Easing.quad),
        // На телефоне прозрачность умеет считать нативный драйвер, и отдавать её ему стоит:
        // анимация не зависит от занятости JS-потока. В вебе такого модуля нет вовсе, и
        // безусловный `true` там означал не ускорение, а предупреждение в консоли на каждый
        // значок («Falling back to JS-based animation») — при том что откат всё равно
        // происходил. Анимация одинакова, разница только в шуме.
        useNativeDriver: Platform.OS !== 'web',
      });
    const pulse = Animated.sequence([step(DIM_OPACITY), step(1)]);
    const run = Animated.loop(pulse, { iterations: PULSES });
    run.start(() => opacity.setValue(1));
    return () => {
      run.stop();
      opacity.setValue(1);
    };
  }, [opacity, reduceMotion]);

  const model = unseenReactionBadgeModel({ emoji });
  if (!model.visible) return null;

  return (
    <Animated.View
      style={[styles.wrap, { opacity }]}
      accessibilityRole="image"
      accessibilityLabel={accessibilityLabel}>
      {hasReactionGlyph(model.emoji) ? (
        <ReactionGlyph emoji={model.emoji} size={GLYPH_SIZE} color={color} />
      ) : (
        // Реакции вне рисованного набора (старые данные, будущее расширение панели) —
        // обесцвеченной эмодзи. Пластика чужая, но показать не ту иконку было бы хуже.
        <Text style={styles.glyph} allowFontScaling={false}>
          {model.emoji}
        </Text>
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrap: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 2 },
  glyph: {
    fontSize: 20,
    // Монохром: значок обязан читаться как часть строки, а не как приклеенный стикер.
    // Синтаксис у платформ разный. Нативный RN ждёт список операций, react-native-web —
    // готовую строку CSS: массив он не переводит и молча оставляет эмодзи цветной (проверено
    // на стенде — сердце осталось красным).
    ...Platform.select({
      web: { filter: 'grayscale(1)' } as object,
      default: { filter: [{ grayscale: 1 }] },
    }),
  },
});
